import { useEffect, useRef } from 'react';

import {
  SEARCHABLE_VENDOR_TAXONOMY_KEYS,
  VENUES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorWordPlural } from '@zola-helpers/client/dist/es/marketplace/vendorWords';

import queryString from 'query-string';

import { loadMiniQuizAnswers } from '~/actions/couples/miniQuizActions';
import { fetchUserSettings } from '~/actions/couples/userSettingsActions';
import {
  addSuccessToastToSessionStorageAndReload,
  useMiniQuizModal,
} from '~/components/common/modals/MiniQuizModal/showMiniQuizModal';
import { useUserContext } from '~/contexts/UserContext';
import { getMiniQuizUserSettingForTaxonomy } from '~/pages/couples/explore/components/MiniQuiz/miniQuizUserSettings';
import { useAppDispatch } from '~/reducers';
import { StandardLogFn } from '~/util/logger';

import { useAnyModalShown } from './useAnyModalShown';
import { useToast } from './useToast';

export const useLaunchMiniQuizFromUrl = () => {
  const dispatch = useAppDispatch();
  const { showMiniQuizModal } = useMiniQuizModal();

  const userContext = useUserContext();

  const anyModalShown = useAnyModalShown();

  const { informationalToast } = useToast();

  const runOnce = useRef(false);

  useEffect(() => {
    if (userContext && !runOnce.current) {
      runOnce.current = true;
      const parsedQueryString = queryString.parse(window.location.search);
      if (Object.keys(parsedQueryString).includes('mini-quiz')) {
        const { 'mini-quiz': miniQuiz, ...query } = parsedQueryString;

        const vendorCategory =
          // First, look for the category in the path (SRP / Category landing page)
          SEARCHABLE_VENDOR_TAXONOMY_KEYS.find((key) => window.location.pathname.includes(key)) ||
          // Second, look for the category as the mini-quiz parameter (?mini-quiz=wedding-photographers)
          SEARCHABLE_VENDOR_TAXONOMY_KEYS.find((key) => key === miniQuiz) ||
          // Finally, default to venues
          VENUES_TAXONOMY_KEY;

        if (!anyModalShown) {
          const miniQuizAnsweredKey = getMiniQuizUserSettingForTaxonomy(vendorCategory);

          dispatch(loadMiniQuizAnswers(vendorCategory, userContext.is_guest))
            .then(() => {
              return dispatch(fetchUserSettings([miniQuizAnsweredKey]));
            })
            .then((response) => {
              const hasAnswered = Boolean(response?.[miniQuizAnsweredKey]?.flag);
              if (!hasAnswered) {
                showMiniQuizModal({
                  taxonomyKey: vendorCategory,
                  ctaPlacement: 'MARKETPLACE',
                  flowEntry: query.utm_medium === 'email' ? 'EMAIL' : 'LANDING_PAGE',
                  businessUnit: 'MARKETPLACE',
                  onQuizSubmitted: addSuccessToastToSessionStorageAndReload,
                });
              } else {
                informationalToast({
                  headline: `You’ve already taken the quiz for ${getVendorWordPlural(
                    vendorCategory
                  )}!”`,
                });
              }
            })
            .catch((error) => {
              StandardLogFn(error);
            });
        }
      }
    }
  }, [anyModalShown, dispatch, informationalToast, showMiniQuizModal, userContext]);
};
